import { Injectable, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DiscountPopupComponent } from '../components/meta/notification-discount/discount-popup/discount-popup.component';
import { isPlatformBrowser } from '@angular/common';
import { AnalyticsService } from './analytics.service';

interface DiscountData {
  code: string;
  expiresAt: Date;
  isFirstVisit: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DiscountCodeManagerService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private bannerDismissed = false;
  private popupShown = false;
  private readonly COOKIE_NAME = 'summer_sale_code';
  private readonly DISCOUNT_TEXT = 'Summer Sale: 10% Rabatt auf alle Produkte!';

  // Public Observable für Banner
  public showBanner$ = new Subject<{
    code: string;
    discountText: string;
    isVisible: boolean;
  }>();

  constructor(
    private dialog: MatDialog,
    private analyticsService: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // === HAUPTFUNKTION ===
  async initializeDiscountSystem(): Promise<DiscountData | null> {
    if (!isPlatformBrowser(this.platformId)) return null;

    try {
      // 1. Prüfe URL für gclid Parameter
      const urlParams = new URLSearchParams(window.location.search);
      const gclid = urlParams.get('gclid');

      // 2. Prüfe existierenden Code im Cookie/Storage
      let discountData = this.getDiscountFromStorage();
      discountData = discountData && this.isDiscountValid(discountData) ? discountData : null;

      if (gclid && !discountData) {
        // User kommt von Google Ads - neuen Code generieren
        discountData = this.generateDiscountCode();
        this.saveDiscountToStorage(discountData);
      }

      if (discountData) {
        this.analyticsService.trackCustomEvent('discount_code_initialized', {
          eventType: 'discount_code',
          customData: {
            discountCode: discountData.code,
            isFirstVisit: discountData.isFirstVisit,
          }
        });
        // Zeige Popup oder Banner
        if (discountData.isFirstVisit && !this.popupShown) {
          this.showPopup(discountData);
        } else if (!this.bannerDismissed) {
          this.showBanner(discountData);
        }
        return discountData;
      }
      return null;
    } catch (error) {
      console.error('Error initializing discount system:', error);
      return null;
    }
  }

  dismissBanner(): void {
    this.bannerDismissed = true;
    this.showBanner$.next({
      code: '',
      discountText: this.DISCOUNT_TEXT,
      isVisible: false
    });
  }

  // === PRIVATE METHODS ===

  private generateDiscountCode(): DiscountData {
    const randomCode = Math.random().toString(36).substring(2, 6).toUpperCase();
    const expiresAt = new Date('2025-08-18T23:59:59.999Z'); // Fixed expiry date: 18.08.2025

    return {
      code: randomCode,
      expiresAt,
      isFirstVisit: true
    };
  }

  private getDiscountFromStorage(): DiscountData | null {
    try {
      // Versuche Cookie
      const cookieValue = this.getCookie(this.COOKIE_NAME);
      if (cookieValue) {
        const data = JSON.parse(cookieValue);
        return {
          code: data.code,
          expiresAt: new Date(data.expiresAt),
          isFirstVisit: false // Nicht mehr erster Besuch
        };
      }

      // Fallback zu localStorage
      const storageValue = localStorage.getItem(this.COOKIE_NAME);
      if (storageValue) {
        const data = JSON.parse(storageValue);
        return {
          code: data.code,
          expiresAt: new Date(data.expiresAt),
          isFirstVisit: false
        };
      }
    } catch (error) {
      console.error('Error reading discount from storage:', error);
    }

    return null;
  }

  private saveDiscountToStorage(discountData: DiscountData): void {
    const dataToStore = {
      code: discountData.code,
      expiresAt: discountData.expiresAt.toISOString()
    };

    try {
      // Speichere als Cookie
      const expires = discountData.expiresAt.toUTCString();
      document.cookie = `${this.COOKIE_NAME}=${encodeURIComponent(JSON.stringify(dataToStore))}; expires=${expires}; path=/; SameSite=Strict`;
      
      // Fallback zu localStorage
      localStorage.setItem(this.COOKIE_NAME, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving discount to storage:', error);
    }
  }

  private isDiscountValid(discountData: DiscountData): boolean {
    return new Date() < discountData.expiresAt;
  }

  private getCookie(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  private showPopup(discountData: DiscountData): void {
    this.popupShown = true;

    const dialogRef = this.dialog.open(DiscountPopupComponent, {
      data: { 
        code: discountData.code,
        discountText: this.DISCOUNT_TEXT
      },
      width: '500px',
      maxWidth: '95vw',
      disableClose: false,
      autoFocus: false
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Nach Popup Banner anzeigen
        if (!this.bannerDismissed) {
          setTimeout(() => {
            this.showBanner(discountData);
          }, 1000);
        }
      });
  }

  private showBanner(discountData: DiscountData): void {
    this.showBanner$.next({
      code: discountData.code,
      discountText: this.DISCOUNT_TEXT,
      isVisible: true
    });
  }
}
